import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import TableGuideLogo from "@/components/brand/TableGuideLogo";
import { Loader2, ShieldCheck } from "lucide-react";

type OAuthNamespace = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function oauth(): OAuthNamespace {
  return (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = "/auth?mode=login&next=" + encodeURIComponent(next);
        return;
      }
      const { data, error } = await oauth().getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (error) {
        setError(error.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error } = approve
      ? await oauth().approveAuthorization(authorizationId)
      : await oauth().denyAuthorization(authorizationId);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("Die Freigabe konnte nicht abgeschlossen werden (keine Weiterleitung erhalten).");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "Diese App";

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-secondary/30">
      <Card className="w-full max-w-md border-border shadow-lg">
        <CardHeader className="text-center">
          <TableGuideLogo className="h-12 w-auto mb-3 mx-auto" position="header" />
          <CardTitle className="text-xl">Zugriff freigeben</CardTitle>
          <CardDescription>
            {error
              ? "Diese Anfrage konnte nicht geladen werden."
              : details
                ? `${clientName} möchte Table-Guide in deinem Namen nutzen.`
                : "Anfrage wird geladen…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && <p className="text-sm text-destructive text-center">{error}</p>}

          {!error && !details && (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          )}

          {!error && details && (
            <>
              <div className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
                <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  {clientName} kann dann Deals und Restaurants durchsuchen sowie deine eigenen Buchungen und
                  Loyalty-Punkte lesen. Du kannst die Verbindung jederzeit in der App widerrufen.
                </p>
              </div>
              <div className="flex gap-2">
                <Button className="flex-1" disabled={busy} onClick={() => decide(true)}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Freigeben"}
                </Button>
                <Button variant="outline" className="flex-1" disabled={busy} onClick={() => decide(false)}>
                  Ablehnen
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
